import React, { useRef, useState } from "react";

import { HubAccessLevel } from "@gamedev-web/one-gaming-crm";
import { Dropdown, Icon, IconSize } from "@gaming/xds-react-js";
import {
  ErrorBadgeSolid,
  Launch,
  MoreVertical,
  PresenceFavoriteSolid,
} from "@gaming/xds-react-js-icons";

import { PageLoadingSpinner } from "../../../../components/PageLoadingSpinner";
import { LoadingSpinner } from "../../../../components/Spinner";
import { useStrings } from "../../../../localization/localizationHelpers";
import type { AdminContact, AdminContactLifecycleAction } from "../../../../models/adminPage";
import { getAvatarClass, getMemberInitials } from "../../avatarHelpers";
import { getFuzzySearchScore } from "../../fuzzySearch";
import {
  type AdminActionConfirmation,
  AdminActionConfirmationModal,
} from "../AdminActionConfirmationModal";
import { AdminMultiSelectDropdown, AdminSelectDropdown } from "../AdminDropdown";
import { AdminTableToolbar } from "../AdminTableToolbar";
import { MemberDetailsModal } from "../MemberDetailsModal";
import { TransferPrimarySignatoryModal } from "../TransferPrimarySignatoryModal";
import * as styles from "../../AdminPage.module.css";

type ContactSortKey = "firstName" | "lastName" | "jobTitle" | "email" | "accessLevel";
type SortDirection = "ascending" | "descending";
const businessContactFilter = "__business_contact__";

type ContactActionMenuProps = Readonly<{
  disabled: boolean;
  canInvite: boolean;
  inviteLabel: string;
  isHubEnabled: boolean;
  moreOptionsLabel: string;
  noLongerAtCompanyLabel: string;
  removeHubAccessLabel: string;
  onAction: (action: AdminContactLifecycleAction) => void;
  onInvite: () => void;
}>;

const ContactActionMenu = ({
  disabled,
  canInvite,
  inviteLabel,
  isHubEnabled,
  moreOptionsLabel,
  noLongerAtCompanyLabel,
  removeHubAccessLabel,
  onAction,
  onInvite,
}: ContactActionMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const requestAction = (action: AdminContactLifecycleAction) => {
    setIsOpen(false);
    onAction(action);
  };

  return (
    <div className={styles.actionMenu}>
      <button
        ref={triggerRef}
        className={styles.actionMenuSummary}
        type="button"
        aria-label={moreOptionsLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={() => setIsOpen(open => !open)}
      >
        <Icon aria-hidden="true" icon={MoreVertical} iconSize={IconSize.XSmall} />
      </button>
      {isOpen && (
        <Dropdown
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          relativeRef={triggerRef as React.RefObject<HTMLElement>}
          horizontalAlignment="right"
          backgroundDismissable
          dimBackground="none"
          className={styles.actionMenuPopup}
          zIndex={1000}
        >
          <div role="menu">
            {isHubEnabled ? (
              <button
                className={styles.actionMenuItem}
                role="menuitem"
                type="button"
                onClick={() => requestAction("removeHubAccess")}
              >
                {removeHubAccessLabel}
              </button>
            ) : (
              <button
                className={styles.actionMenuItem}
                role="menuitem"
                type="button"
                disabled={!canInvite}
                onClick={() => {
                  setIsOpen(false);
                  onInvite();
                }}
              >
                {inviteLabel}
              </button>
            )}
            <button
              className={styles.actionMenuItem}
              role="menuitem"
              type="button"
              onClick={() => requestAction("removeFromCompany")}
            >
              {noLongerAtCompanyLabel}
            </button>
          </div>
        </Dropdown>
      )}
    </div>
  );
};

type ActiveContactsPanelProps = Readonly<{
  contacts: AdminContact[] | null;
  canChangePrimarySignatory: boolean;
  canInvite: boolean;
  error: boolean;
  isLoading: boolean;
  isTransferringPrimarySignatory: boolean;
  isVisible: boolean;
  readOnly?: boolean;
  showPrimarySignatorySection: boolean;
  onRefresh: () => void;
  onRemoveFromCompany: (contactId: string) => void;
  onRemoveHubAccess: (contactId: string) => void;
  onTransferPrimarySignatory: (contactId: string) => void;
  onUpdateContactAccess: (contactId: string, value: HubAccessLevel) => void;
  onInvite: (contact: AdminContact) => void;
  updatingContactIds: ReadonlySet<string>;
}>;

export const ActiveContactsPanel = ({
  contacts,
  canChangePrimarySignatory,
  canInvite,
  error,
  isLoading,
  isTransferringPrimarySignatory,
  isVisible,
  readOnly = false,
  showPrimarySignatorySection,
  onRefresh,
  onRemoveFromCompany,
  onRemoveHubAccess,
  onTransferPrimarySignatory,
  onUpdateContactAccess,
  onInvite,
  updatingContactIds,
}: ActiveContactsPanelProps) => {
  const [
    activeMembersTitle,
    activeMembersEmptyLabel,
    activeMembersLoadingLabel,
    activeMembersErrorLabel,
    firstNameLabel,
    lastNameLabel,
    jobTitleLabel,
    emailLabel,
    accessLevelLabel,
    gameConceptAdminLabel,
    gameManagerLabel,
    submittedConceptsOnlyLabel,
    searchLabel,
    filterAccessLevelLabel,
    noResultsLabel,
    openCrmLabel,
    closeSearchLabel,
    refreshLabel,
    removeHubAccessLabel,
    noLongerAtCompanyLabel,
    moreOptionsLabel,
    inviteLabel,
    businessContactLabel,
    noHubAccessLabel,
    youLabel,
    sortByLabel,
    sortAscendingLabel,
    sortDescendingLabel,
    roleFilterLabel,
    primarySignatoryLabel,
  ] = useStrings(
    "partnerAdminActiveMembersTitle",
    "partnerAdminActiveMembersEmptyLabel",
    "partnerAdminLoadingLabel",
    "partnerAdminErrorLabel",
    "partnerAdminFirstNameLabel",
    "partnerAdminLastNameLabel",
    "partnerAdminJobTitleLabel",
    "partnerAdminEmailLabel",
    "partnerAdminAccessLevelLabel",
    "partnerAdminGameConceptAdminAccessLabel",
    "partnerAdminGameManagerAccessLabel",
    "partnerAdminSubmittedConceptsOnlyAccessLabel",
    "partnerAdminMemberSearchLabel",
    "partnerAdminFilterAccessLevelLabel",
    "noResultsFound",
    "partnerAdminOpenCrmLabel",
    "closeSearchAriaLabel",
    "partnerAdminRefreshLabel",
    "partnerAdminRemoveHubAccessLabel",
    "partnerAdminNoLongerAtCompanyLabel",
    "moreOptionsAriaLabel",
    "partnerAdminInviteLabel",
    "partnerAdminBusinessContactLabel",
    "partnerAdminNotHubEnabledLabel",
    "partnerAdminYouLabel",
    "partnerAdminSortByLabel",
    "partnerAdminSortAscendingLabel",
    "partnerAdminSortDescendingLabel",
    "partnerAdminRoleFilterLabel",
    "partnerAdminPrimarySignatoryLabel"
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [accessFilters, setAccessFilters] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<ContactSortKey>("accessLevel");
  const [sortDirection, setSortDirection] = useState<SortDirection>("ascending");
  const [confirmation, setConfirmation] = useState<AdminActionConfirmation | null>(null);
  const [selectedContact, setSelectedContact] = useState<AdminContact | null>(null);
  const [isTransferOpen, setIsTransferOpen] = useState(false);

  const accessLevelLabels: Record<HubAccessLevel, string> = {
    [HubAccessLevel.Admin]: gameConceptAdminLabel,
    [HubAccessLevel.GameManager]: gameManagerLabel,
    [HubAccessLevel.User]: submittedConceptsOnlyLabel,
  };
  const accessLevelOptions = [
    { id: String(HubAccessLevel.Admin), label: gameConceptAdminLabel },
    { id: String(HubAccessLevel.GameManager), label: gameManagerLabel },
    { id: String(HubAccessLevel.User), label: submittedConceptsOnlyLabel },
  ];
  const accessFilterOptions = [
    ...accessLevelOptions,
    { id: businessContactFilter, label: businessContactLabel },
  ];
  const sortOptions: Array<{ id: ContactSortKey; label: string }> = [
    { id: "firstName", label: firstNameLabel },
    { id: "lastName", label: lastNameLabel },
    { id: "jobTitle", label: jobTitleLabel },
    { id: "email", label: emailLabel },
    { id: "accessLevel", label: accessLevelLabel },
  ];
  const panelContacts = contacts ?? [];
  const transferTargets = panelContacts.filter(
    contact => contact.isAccountContact !== false && !contact.isPrimarySignatory
  );
  const hasSearch = searchQuery.trim().length > 0;
  const getAccessLevelLabel = (contact: AdminContact) =>
    contact.isHubEnabled
      ? accessLevelLabels[contact.hubRole ?? HubAccessLevel.User]
      : noHubAccessLabel;
  const getSortValue = (contact: AdminContact) =>
    sortKey === "accessLevel" ? getAccessLevelLabel(contact) : contact[sortKey];
  const visibleContacts = panelContacts
    .map(contact => ({
      contact,
      searchScore: getFuzzySearchScore(searchQuery, [
        contact.firstName,
        contact.lastName,
        `${contact.firstName} ${contact.lastName}`,
        contact.email,
        contact.jobTitle,
      ]),
    }))
    .filter(
      ({ contact, searchScore }) =>
        searchScore !== undefined &&
        (accessFilters.length === 0 ||
          (contact.isHubEnabled && accessFilters.includes(String(contact.hubRole))) ||
          (!contact.isHubEnabled && accessFilters.includes(businessContactFilter)))
    )
    .sort((left, right) => {
      if (hasSearch && left.searchScore !== right.searchScore) {
        return (left.searchScore ?? 0) - (right.searchScore ?? 0);
      }
      if (sortKey === "accessLevel" && left.contact.isHubEnabled !== right.contact.isHubEnabled) {
        return left.contact.isHubEnabled ? -1 : 1;
      }
      const comparison = getSortValue(left.contact).localeCompare(
        getSortValue(right.contact),
        undefined,
        {
          sensitivity: "base",
        }
      );
      return sortDirection === "ascending" ? comparison : -comparison;
    })
    .map(({ contact }) => contact);
  const showCrmLinks = visibleContacts.some(contact => contact.crmUrl);

  if (!isVisible) {
    return null;
  }

  return (
    <section role="tabpanel" aria-label={activeMembersTitle}>
      <AdminTableToolbar
        hasActiveFilters={hasSearch || accessFilters.length > 0}
        closeSearchLabel={closeSearchLabel}
        isRefreshing={isLoading}
        onClear={() => {
          setSearchQuery("");
          setAccessFilters([]);
        }}
        onRefresh={onRefresh}
        onSearchChange={setSearchQuery}
        refreshLabel={refreshLabel}
        resultCount={visibleContacts.length}
        searchLabel={searchLabel}
        searchQuery={searchQuery}
      >
        <AdminMultiSelectDropdown
          ariaLabel={filterAccessLevelLabel}
          label={roleFilterLabel}
          options={accessFilterOptions}
          values={accessFilters}
          onChange={setAccessFilters}
        />
        <AdminSelectDropdown
          ariaLabel={sortByLabel}
          label={sortByLabel}
          options={sortOptions}
          value={sortKey}
          onChange={value => {
            setSortKey(value);
            setSortDirection("ascending");
          }}
        />
        <button
          className={styles.sortDirectionButton}
          type="button"
          aria-label={sortDirection === "ascending" ? sortDescendingLabel : sortAscendingLabel}
          onClick={() =>
            setSortDirection(current => (current === "ascending" ? "descending" : "ascending"))
          }
        >
          {sortDirection === "ascending" ? sortAscendingLabel : sortDescendingLabel}
        </button>
      </AdminTableToolbar>
      {isLoading || (contacts === null && !error) ? (
        <div
          role="status"
          aria-live="polite"
          aria-label={activeMembersLoadingLabel}
          aria-busy="true"
        >
          <PageLoadingSpinner />
        </div>
      ) : error ? (
        <p className={styles.message} role="alert">
          {activeMembersErrorLabel}
        </p>
      ) : panelContacts.length === 0 ? (
        <p className={styles.message}>{activeMembersEmptyLabel}</p>
      ) : (
        <>
          {visibleContacts.length === 0 ? (
            <p className={styles.message}>{noResultsLabel}</p>
          ) : (
            <ul className={styles.memberGrid} aria-label={activeMembersTitle}>
              {visibleContacts.map(contact => {
                const isUpdating = updatingContactIds.has(contact.contactId);
                return (
                  <li className={styles.memberCard} key={contact.contactId} aria-busy={isUpdating}>
                    <div className={styles.memberCardHeader}>
                      <button
                        type="button"
                        className={styles.memberCardOpen}
                        onClick={() => setSelectedContact(contact)}
                      >
                        <div
                          className={`${styles.memberAvatar} ${getAvatarClass(contact.contactId)}`}
                          aria-hidden="true"
                        >
                          {getMemberInitials(contact)}
                        </div>
                        <div className={styles.memberIdentity}>
                          <div className={styles.memberNameRow}>
                            <h3 className={styles.memberName}>
                              <span>{contact.firstName}</span> <span>{contact.lastName}</span>
                              {contact.isCurrentUser && (
                                <span className={styles.currentUserLabel}>({youLabel})</span>
                              )}
                            </h3>
                            {contact.isPrimarySignatory && (
                              <span className={styles.primarySignatoryBadge}>
                                <Icon
                                  aria-hidden="true"
                                  className={styles.primarySignatoryIcon}
                                  icon={PresenceFavoriteSolid}
                                  iconSize={IconSize.Custom}
                                />
                                {primarySignatoryLabel}
                              </span>
                            )}
                          </div>
                          <span className={styles.memberEmail}>{contact.email}</span>
                          {contact.jobTitle && (
                            <span className={styles.memberJobTitle}>{contact.jobTitle}</span>
                          )}
                        </div>
                      </button>
                      <div className={styles.memberCardActions}>
                        {isUpdating && (
                          <span
                            className={styles.cardSpinner}
                            role="status"
                            aria-label={activeMembersLoadingLabel}
                          >
                            <LoadingSpinner />
                          </span>
                        )}
                        {showCrmLinks && contact.crmUrl && (
                          <a
                            className={styles.crmLink}
                            href={contact.crmUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={openCrmLabel}
                          >
                            <Icon aria-hidden="true" icon={Launch} iconSize={IconSize.XSmall} />
                          </a>
                        )}
                        {!readOnly &&
                          contact.isAccountContact !== false &&
                          !contact.isCurrentUser && (
                            <ContactActionMenu
                              disabled={isUpdating}
                              canInvite={canInvite}
                              inviteLabel={inviteLabel}
                              isHubEnabled={contact.isHubEnabled}
                              moreOptionsLabel={moreOptionsLabel}
                              noLongerAtCompanyLabel={noLongerAtCompanyLabel}
                              removeHubAccessLabel={removeHubAccessLabel}
                              onAction={action => setConfirmation({ action, member: contact })}
                              onInvite={() => onInvite(contact)}
                            />
                          )}
                      </div>
                    </div>
                    <div className={styles.memberCardFooter}>
                      {contact.isHubEnabled ? (
                        <div className={styles.memberRole}>
                          <span className={styles.memberRoleValue}>
                            {getAccessLevelLabel(contact)}
                          </span>
                        </div>
                      ) : (
                        <div className={styles.memberRole}>
                          <span className={styles.memberRoleLabel}>{businessContactLabel}</span>
                          <span className={styles.memberBadges}>
                            <span className={`${styles.memberBadge} ${styles.noHubAccessBadge}`}>
                              <Icon
                                aria-hidden="true"
                                icon={ErrorBadgeSolid}
                                iconSize={IconSize.XSmall}
                              />
                              {noHubAccessLabel}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
      <AdminActionConfirmationModal
        confirmation={confirmation}
        onCancel={() => setConfirmation(null)}
        onConfirm={selected => {
          if (selected.action === "revokeInvitation" || selected.action === "resendInvitation") {
            return;
          }
          setConfirmation(null);
          if (selected.action === "changeRole") {
            onUpdateContactAccess(selected.member.contactId, selected.accessLevel);
            return;
          }
          if (selected.action === "transferPrimarySignatory") {
            onTransferPrimarySignatory(selected.member.contactId);
            return;
          }
          const callback =
            selected.action === "removeHubAccess" ? onRemoveHubAccess : onRemoveFromCompany;
          callback(selected.member.contactId);
        }}
      />
      <TransferPrimarySignatoryModal
        contacts={transferTargets}
        isBusy={isTransferringPrimarySignatory}
        isOpen={isTransferOpen}
        onCancel={() => setIsTransferOpen(false)}
        onSelect={target => {
          setIsTransferOpen(false);
          setConfirmation({ action: "transferPrimarySignatory", member: target });
        }}
      />
      <MemberDetailsModal
        key={selectedContact?.contactId ?? "closed"}
        canTransferPrimarySignatory={
          canChangePrimarySignatory && !isTransferringPrimarySignatory && transferTargets.length > 0
        }
        contact={selectedContact}
        readOnly={readOnly}
        showPrimarySignatorySection={showPrimarySignatorySection}
        onClose={() => setSelectedContact(null)}
        onChangeRole={onUpdateContactAccess}
        onRemove={contact => {
          setSelectedContact(null);
          setConfirmation({ action: "removeFromCompany", member: contact });
        }}
        onTransferPrimarySignatory={() => {
          setSelectedContact(null);
          setIsTransferOpen(true);
        }}
      />
    </section>
  );
};
